<template>
  <LoadingDiv v-if="isInitialLoading" />

  <template v-else>
    <div class="claim-history-alert error" role="alert" v-if="error">
      History could not be loaded.
    </div>
    <div class="claim-history-alert warning" v-if="sourceWarning">
      {{ sourceWarning }}
    </div>

    <div
      class="card z-depth-0 claim-history-card"
      v-for="(claim, index) in claims"
      :key="`${claim.history_source || 'easyhmo'}:${claim.id || index}`"
    >
      <div class="card-panel z-depth-0 claim-history-panel">
        <div class="row">
          <div class="col s3 hide-on-small-only">
            <h6>
              {{ claim.encounter.name || "Enrollee" }}
              <small>
                <span class="blue-text">
                  {{ showAgeSex(claim.encounter.dob, claim.encounter.sex) }}
                </span>
              </small>
              <br />
              <small>{{ claim.encounter.enrollee_number || "ID not recorded" }}</small>
              <br />
              <small v-if="claim.encounter.client_name">({{ claim.encounter.client_name }})</small>
            </h6>
          </div>
          <div class="col s5 m2">
            <p>
              {{ claim.encounter.provider.name || claim.encounter.provider_name || "Provider not recorded" }}
            </p>
          </div>
          <div class="col s7 m2">
            <p><b>{{ claim.encounter.diagnosis || "Diagnosis not recorded" }}</b></p>
          </div>
          <div class="col s12 m2">
            <p>
              {{ claim.item }}
              <span class="black-text">(x<b>{{ claim.sent_qty }}</b>)</span>
              <br />
              <span
                v-if="claim.sent_price"
                :class="{
                  'amber-text text-darken-4': claim.authorization === 'z' && claim.status === 'a',
                  'black-text': !(claim.authorization === 'z' && claim.status === 'a'),
                }"
              >
                &#8358;{{ formatMoney(claim.sent_price) }}
              </span>
            </p>
          </div>
          <div class="col s7 m2">
            <p>
              <span v-if="claim.authorization === 'z'">
                <span class="chip" v-if="claim.status === 'a'">Pending</span>
                <span class="chip red lighten-2" v-else>Declined</span>
              </span>
              <span v-else>
                {{ claim.authorization_code }}<br />
                <span class="chip green lighten-2" v-if="claim.approved_price">
                  <b>&#8358;{{ formatMoney(claim.approved_price) }}</b>
                </span>
                <span v-else>&#8358;0.00</span>
              </span>
            </p>
          </div>
          <div class="col s5 m1">
            <p
              class="right"
              :class="{
                'black-text': claim.authorization === 'z',
                'green-text': claim.authorization !== 'z',
              }"
            >
              {{ formatHistoryDate(claim.created_at) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="section center" v-if="nextLoad">
      <button
        class="btn-flat waves-effect history-load-more"
        :class="{ disabled: isLoading }"
        @click.prevent="handlePage(nextLoad)"
      >
        <span v-if="!isLoading">{{ error ? "Retry history" : "Load more history" }}</span>
        <span v-else>Loading history...</span>
      </button>
      <div class="preloader-wrapper small active history-inline-loader" v-if="isLoading">
        <div class="spinner-layer spinner-blue-only">
          <div class="circle-clipper left"><div class="circle"></div></div>
          <div class="gap-patch"><div class="circle"></div></div>
          <div class="circle-clipper right"><div class="circle"></div></div>
        </div>
      </div>
    </div>
  </template>

  <div
    class="claim-history-empty-note"
    v-if="!hideUnFound && !claims.length && !isLoading && hasLoaded && !error"
  >
    <i class="material-icons">info_outline</i>
    <span>Authorization history is not available for this selected claim.</span>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { ageSex } from "@/composables/fxLibrary/coreFx";
import getLocalItems from "@/composables/getItems";
import { siloMode } from "@/laravel/config";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "HistoryClaimComponent",
  components: { LoadingDiv },
  props: ["enrolleeNumber", "schemeId", "hideUnFound"],
  setup(props) {
    const response = ref([]);
    const error = ref(false);
    const isLoading = ref(false);
    const hasLoaded = ref(false);
    const nextLoad = ref(null);
    const localHistoryStarted = ref(siloMode);
    const sourceWarning = ref(null);

    const normalizeClaim = (claim, source) => {
      const safeClaim = claim && typeof claim === "object" ? claim : {};
      const encounter = safeClaim.encounter && typeof safeClaim.encounter === "object"
        ? safeClaim.encounter
        : {};
      const provider = encounter.provider && typeof encounter.provider === "object"
        ? encounter.provider
        : {};

      return {
        ...safeClaim,
        encounter: { ...encounter, provider },
        history_source: source,
      };
    };

    const appendRows = (rows, source) => {
      const normalized = (Array.isArray(rows) ? rows : [])
        .map(claim => normalizeClaim(claim, source));
      response.value = [...response.value, ...normalized];
    };

    const pageFromResponse = (payload) => {
      const page = payload?.data;

      if (Array.isArray(page)) {
        return { rows: page, currentPage: 1, lastPage: 1 };
      }

      return {
        rows: Array.isArray(page?.data) ? page.data : [],
        currentPage: Number(page?.current_page || 1),
        lastPage: Number(page?.last_page || 1),
      };
    };

    const historyQuery = (page) => {
      const query = new URLSearchParams({
        e: props.enrolleeNumber || "",
        page: String(page),
      });
      const schemeId = Number(props.schemeId);

      if (schemeId > 0) {
        query.set(siloMode ? "s" : "scheme_id", String(schemeId));
      }

      if (!siloMode) {
        query.set("submission_scope", "submitted");
      }

      return query;
    };

    const loadLocalHistory = async (page) => {
      isLoading.value = true;
      error.value = false;

      try {
        const query = historyQuery(page);
        const { res, err } = await getLocalItems(`claim/auth/enrollee?${query.toString()}`);

        if (!res.value) {
          error.value = err.value || "History could not be loaded.";
          nextLoad.value = page;
          return;
        }

        const localPage = pageFromResponse(res.value);
        appendRows(localPage.rows, "easyhmo");
        nextLoad.value = localPage.lastPage > localPage.currentPage
          ? localPage.currentPage + 1
          : null;
      } catch (requestError) {
        error.value = requestError?.message || "History could not be loaded.";
        nextLoad.value = page;
      } finally {
        isLoading.value = false;
        hasLoaded.value = true;
      }
    };

    const loadOneProviderHistory = async () => {
      isLoading.value = true;

      try {
        const query = new URLSearchParams({ e: props.enrolleeNumber || "" });
        const schemeId = Number(props.schemeId);

        if (schemeId > 0) {
          query.set("scheme_id", String(schemeId));
        }

        const { res, err } = await getLocalItems(
          `integration/oneprovider/auth/history?${query.toString()}`
        );
        const remotePage = pageFromResponse(res.value);

        if (remotePage.rows.length) {
          appendRows(remotePage.rows, "oneprovider");
          nextLoad.value = 1;
          hasLoaded.value = true;
          return;
        }

        if (!res.value) {
          error.value = err.value;
          sourceWarning.value = "Recent oneProvider history is unavailable. Showing submitted history.";
        }
      } catch (requestError) {
        error.value = requestError?.message || "Recent oneProvider history is unavailable.";
        sourceWarning.value = "Recent oneProvider history is unavailable. Showing submitted history.";
      } finally {
        isLoading.value = false;
      }

      localHistoryStarted.value = true;
      await loadLocalHistory(1);
    };

    const handlePage = (page) => {
      if (isLoading.value) {
        return;
      }

      if (!siloMode && !localHistoryStarted.value) {
        localHistoryStarted.value = true;
      }

      loadLocalHistory(page);
    };

    const showAgeSex = (dob, sex) => ageSex(dob, sex).fx.value;

    const formatMoney = value => Number(value || 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const formatHistoryDate = (value) => {
      const date = new Date(value);

      if (!value || Number.isNaN(date.getTime())) {
        return "Date not recorded";
      }

      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    onMounted(() => {
      if (siloMode) {
        loadLocalHistory(1);
      } else {
        loadOneProviderHistory();
      }
    });

    return {
      claims: computed(() => response.value),
      error,
      formatHistoryDate,
      formatMoney,
      handlePage,
      hasLoaded,
      isInitialLoading: computed(() => isLoading.value && !hasLoaded.value),
      isLoading,
      nextLoad,
      showAgeSex,
      sourceWarning,
    };
  },
};
</script>

<style scoped>
.claim-history-card {
  background: #ffffff;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none;
  margin: 10px 0 0;
  overflow: hidden;
}

.claim-history-panel {
  background: #ffffff;
  box-shadow: none;
  margin: 0;
}

.claim-history-panel .row {
  margin-bottom: 0;
}

.claim-history-alert {
  padding: 12px;
  text-align: center;
}

.claim-history-alert.error {
  color: #c62828;
}

.claim-history-alert.warning {
  color: #8d6e00;
}

.claim-history-empty-note {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  color: #607d8b;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-height: 90px;
  padding: 18px;
}

.claim-history-empty-note i {
  color: #00897b;
  font-size: 20px;
}

.history-load-more {
  text-transform: none;
}

.history-inline-loader {
  display: block;
  margin: 10px auto 0;
}
</style>
